import Vue from 'vue';

const initialState = () => ({
  loading: false,
  error: null,
  soapNoteRequired: false,
  appointmentId: null,
  case: null,
  medicationClasses: [],
  dmeProducts: [],
  currentScreen: 1,
  draftCase: {},
  draftMedicationSelections: {},
  draftDmeSelections: {},
});

const buildMedDraftFromClass = (cls, primaryTemplateId) => {
  if (!cls) return null;
  const templates = Array.isArray(cls.templates) ? cls.templates : [];
  const isPrefilled = cls.selectionMode === 'prefilled';
  const primaryTpl = isPrefilled
    ? templates.find(t => t.isPrimaryDefault)
    : templates.find(t => t.id === primaryTemplateId);
  if (!primaryTpl) return null;
  const altTemplates = isPrefilled
    ? templates.filter(t => t.canBeAlternative && t.id !== primaryTpl.id)
    : templates.filter(t => t.id !== primaryTpl.id);
  return {
    selected: true,
    classId: cls.id,
    classCode: cls.code,
    primaryMedicationTemplateId: primaryTpl.id,
    primaryMedication: {
      name: primaryTpl.name || '',
      strength: primaryTpl.strength || '',
      dosageForm: primaryTpl.dosageForm || '',
      sig: primaryTpl.defaultSig || '',
      quantity: primaryTpl.defaultQuantity || '',
      refills: primaryTpl.defaultRefills || '0',
    },
    authorizedAlternatives: altTemplates.map(tpl => ({
      templateId: tpl.id,
      include: false,
      name: tpl.name || '',
      strength: tpl.strength || '',
      dosageForm: tpl.dosageForm || '',
      sig: tpl.defaultSig || '',
      quantity: tpl.defaultQuantity || '',
      refills: tpl.defaultRefills || '0',
    })),
    disauthorizedSubstitutions: '',
    physicianConfirmations: {},
  };
};

const buildDmeDraftFromProduct = (product) => {
  if (!product) return null;
  return {
    selected: true,
    dmeId: product.id,
    dmeCode: product.code,
    productName: product.productName || '',
    treatmentPlanLabel: product.treatmentPlanLabel || '',
    surgicalStatus: 'non_surgical',
    surgeryDate: null,
    icd10Codes: [],
    treatmentAreas: [],
    treatmentAreaOther: '',
    orientation: 'na',
    durationMonths: product.code === 'CCT' ? 3 : null,
    extraGelPacks: product.code === 'CCT' ? 2 : null,
    frequency: { ...(product.defaultFrequency || {}) },
    conditionRelatedTo: null,
    conditionRelatedToOther: '',
    treatmentGoals: [],
    treatmentGoalOther: '',
    patientEducationRequired: false,
    physicianConfirmations: {},
  };
};

const serializeMedicationDraftsForApi = (drafts) => {
  if (!drafts) return [];
  return Object.entries(drafts).map(([classCode, draft]) => {
    if (draft === null || draft === undefined) {
      return { classCode, _delete: true };
    }
    return {
      classCode,
      primaryMedicationTemplateId: draft.primaryMedicationTemplateId ?? null,
      primaryMedication: draft.primaryMedication || {},
      authorizedAlternatives: draft.authorizedAlternatives || [],
      disauthorizedSubstitutions: draft.disauthorizedSubstitutions || '',
      physicianConfirmations: draft.physicianConfirmations || {},
    };
  });
};

const serializeDmeDraftsForApi = (drafts) => {
  if (!drafts) return [];
  return Object.entries(drafts).map(([dmeCode, draft]) => {
    if (draft === null || draft === undefined) {
      return { dmeCode, _delete: true };
    }
    const payload = {
      dmeCode,
      surgicalStatus: draft.surgicalStatus ?? null,
      surgeryDate: draft.surgeryDate ?? null,
      icd10Codes: Array.isArray(draft.icd10Codes) ? draft.icd10Codes : [],
      treatmentAreas: Array.isArray(draft.treatmentAreas) ? draft.treatmentAreas : [],
      treatmentAreaOther: draft.treatmentAreaOther || '',
      orientation: draft.orientation ?? null,
      frequency: draft.frequency || {},
      conditionRelatedTo: draft.conditionRelatedTo ?? null,
      conditionRelatedToOther: draft.conditionRelatedToOther || '',
      treatmentGoals: Array.isArray(draft.treatmentGoals) ? draft.treatmentGoals : [],
      treatmentGoalOther: draft.treatmentGoalOther || '',
      patientEducationRequired: draft.patientEducationRequired !== false,
      physicianConfirmations: draft.physicianConfirmations || {},
    };
    if (dmeCode === 'CCT') {
      payload.durationMonths = draft.durationMonths ?? 3;
      payload.extraGelPacks = draft.extraGelPacks ?? 2;
    }
    return payload;
  });
};

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    SET_LOADING: (state, loading) => { state.loading = !!loading; },
    SET_ERROR: (state, error) => { state.error = error || null; },
    SET_SOAP_REQUIRED: (state, value) => { state.soapNoteRequired = !!value; },
    SET_APPOINTMENT_ID: (state, value) => { state.appointmentId = value; },
    SET_CASE: (state, payload) => { state.case = payload || null; },
    SET_MEDICATION_CLASSES: (state, classes) => { state.medicationClasses = Array.isArray(classes) ? classes : []; },
    SET_DME_PRODUCTS: (state, products) => { state.dmeProducts = Array.isArray(products) ? products : []; },
    SET_CURRENT_SCREEN: (state, screen) => { state.currentScreen = screen; },
    SET_DRAFT_CASE: (state, draft) => { state.draftCase = draft || {}; },
    MERGE_DRAFT_CASE: (state, patch) => {
      const next = { ...(state.draftCase || {}) };
      Object.keys(patch || {}).forEach((key) => {
        const value = patch[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          next[key] = { ...(next[key] || {}), ...value };
        } else {
          next[key] = value;
        }
      });
      state.draftCase = next;
    },
    CLEAR_DRAFT_CASE: (state) => { state.draftCase = {}; },
    SET_DRAFT_MED_SELECTION: (state, { classCode, value }) => {
      const next = { ...(state.draftMedicationSelections || {}) };
      next[classCode] = value;
      state.draftMedicationSelections = next;
    },
    MERGE_DRAFT_MED_SELECTION: (state, { classCode, patch }) => {
      const next = { ...(state.draftMedicationSelections || {}) };
      const current = next[classCode];
      if (!current) return;
      next[classCode] = { ...current, ...patch };
      state.draftMedicationSelections = next;
    },
    CLEAR_DRAFT_MED_SELECTIONS: (state) => { state.draftMedicationSelections = {}; },
    SET_DRAFT_DME_SELECTION: (state, { dmeCode, value }) => {
      const next = { ...(state.draftDmeSelections || {}) };
      next[dmeCode] = value;
      state.draftDmeSelections = next;
    },
    MERGE_DRAFT_DME_SELECTION: (state, { dmeCode, patch }) => {
      const next = { ...(state.draftDmeSelections || {}) };
      const current = next[dmeCode];
      if (!current) return;
      next[dmeCode] = { ...current, ...patch };
      state.draftDmeSelections = next;
    },
    CLEAR_DRAFT_DME_SELECTIONS: (state) => { state.draftDmeSelections = {}; },
    RESET: (state) => {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async loadCase({ commit, state }, appointmentId) {
      commit('SET_APPOINTMENT_ID', appointmentId);
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      commit('SET_SOAP_REQUIRED', false);
      commit('CLEAR_DRAFT_CASE');
      commit('CLEAR_DRAFT_MED_SELECTIONS');
      commit('CLEAR_DRAFT_DME_SELECTIONS');
      try {
        const response = await Vue.prototype.$api.prescriptionDme.getCase(appointmentId);
        commit('SET_CASE', response.data && response.data.payload ? response.data.payload : response.data);
      } catch (err) {
        const status = err && err.response && err.response.status;
        const code = err && err.response && err.response.data && (err.response.data.code || err.response.data.payload?.code);
        if (status === 409 && (code === 'SOAP_NOTE_REQUIRED' || /soap_note_required/i.test(JSON.stringify(err.response.data || {})))) {
          commit('SET_SOAP_REQUIRED', true);
        } else {
          commit('SET_ERROR', err);
          throw err;
        }
      } finally {
        commit('SET_LOADING', false);
      }
      return state.case;
    },
    async loadCatalogs({ commit, state }, appointmentId) {
      const id = appointmentId || state.appointmentId;
      if (!id) return;
      const [classes, products] = await Promise.all([
        Vue.prototype.$api.prescriptionDme.listMedicationClasses(id),
        Vue.prototype.$api.prescriptionDme.listDmeProducts(id),
      ]);
      commit('SET_MEDICATION_CLASSES', classes.data?.payload ?? classes.data ?? []);
      commit('SET_DME_PRODUCTS', products.data?.payload ?? products.data ?? []);
    },
    updateDraft({ commit }, patch) {
      commit('MERGE_DRAFT_CASE', patch || {});
    },
    clearDraft({ commit }) {
      commit('CLEAR_DRAFT_CASE');
    },
    selectMedicationClass({ commit, state, getters }, { classCode, primaryMedicationTemplateId }) {
      const cls = (state.medicationClasses || []).find(c => c.code === classCode);
      if (!cls) return null;
      const existingDraft = (state.draftMedicationSelections || {})[classCode];
      const existingPersisted = getters.persistedMedicationSelectionByCode(classCode);
      const isEditableDropdown = cls.selectionMode === 'editable_dropdown';
      let value;
      if (existingDraft && existingDraft.selected) {
        if (isEditableDropdown
            && primaryMedicationTemplateId != null
            && primaryMedicationTemplateId !== existingDraft.primaryMedicationTemplateId) {
          value = buildMedDraftFromClass(cls, primaryMedicationTemplateId);
        } else {
          value = primaryMedicationTemplateId != null
            ? { ...existingDraft, primaryMedicationTemplateId }
            : existingDraft;
        }
      } else if (existingPersisted && existingPersisted.selected && existingDraft !== null) {
        if (isEditableDropdown
            && primaryMedicationTemplateId != null
            && primaryMedicationTemplateId !== existingPersisted.primaryMedicationTemplateId) {
          value = buildMedDraftFromClass(cls, primaryMedicationTemplateId);
        } else {
          value = { ...existingPersisted, selected: true };
          if (primaryMedicationTemplateId != null) value.primaryMedicationTemplateId = primaryMedicationTemplateId;
        }
      } else {
        value = buildMedDraftFromClass(cls, primaryMedicationTemplateId);
      }
      if (!value) return null;
      commit('SET_DRAFT_MED_SELECTION', { classCode, value });
      return value;
    },
    deselectMedicationClass({ commit }, classCode) {
      commit('SET_DRAFT_MED_SELECTION', { classCode, value: null });
    },
    updateMedicationSelectionDraft({ commit, state, getters }, { classCode, patch }) {
      const existingDraft = (state.draftMedicationSelections || {})[classCode];
      if (existingDraft && existingDraft.selected) {
        commit('MERGE_DRAFT_MED_SELECTION', { classCode, patch });
        return;
      }
      const persisted = getters.persistedMedicationSelectionByCode(classCode);
      if (persisted && persisted.selected) {
        const value = { ...persisted, ...patch, selected: true };
        commit('SET_DRAFT_MED_SELECTION', { classCode, value });
      }
    },
    selectDmeProduct({ commit, state, getters }, { dmeCode }) {
      const product = (state.dmeProducts || []).find(p => p.code === dmeCode);
      if (!product) return null;
      const existingDraft = (state.draftDmeSelections || {})[dmeCode];
      const existingPersisted = getters.persistedDmeSelectionByCode(dmeCode);
      let value;
      if (existingDraft && existingDraft.selected) {
        value = existingDraft;
      } else if (existingPersisted && existingPersisted.selected && existingDraft !== null) {
        value = { ...existingPersisted, selected: true };
      } else {
        value = buildDmeDraftFromProduct(product);
      }
      if (!value) return null;
      commit('SET_DRAFT_DME_SELECTION', { dmeCode, value });
      return value;
    },
    deselectDmeProduct({ commit }, dmeCode) {
      commit('SET_DRAFT_DME_SELECTION', { dmeCode, value: null });
    },
    updateDmeSelectionDraft({ commit, state, getters }, { dmeCode, patch }) {
      const existingDraft = (state.draftDmeSelections || {})[dmeCode];
      if (existingDraft && existingDraft.selected) {
        commit('MERGE_DRAFT_DME_SELECTION', { dmeCode, patch });
        return;
      }
      const persisted = getters.persistedDmeSelectionByCode(dmeCode);
      if (persisted && persisted.selected) {
        const value = { ...persisted, ...patch, selected: true };
        commit('SET_DRAFT_DME_SELECTION', { dmeCode, value });
      }
    },
    async saveMedicationDraft({ commit, state }) {
      if (!state.appointmentId) return null;
      const appointmentId = state.appointmentId;
      const api = Vue.prototype.$api.prescriptionDme;

      const draft = state.draftCase || {};
      if (draft && Object.keys(draft).length > 0) {
        await api.patchCase(appointmentId, draft);
      }

      const medBundles = serializeMedicationDraftsForApi(state.draftMedicationSelections)
        .filter(b => b && typeof b.classCode === 'string');
      await Promise.all(medBundles.map((bundle) => {
        if (bundle._delete) {
          return api.removeMedicationSelection(appointmentId, bundle.classCode);
        }
        const { classCode, _delete: _del, ...selectionPayload } = bundle;
        return api.upsertMedicationSelection(appointmentId, classCode, selectionPayload);
      }));

      commit('CLEAR_DRAFT_CASE');
      commit('CLEAR_DRAFT_MED_SELECTIONS');

      const refetched = await api.getCase(appointmentId);
      const fresh = refetched.data?.payload ?? refetched.data;
      commit('SET_CASE', fresh);
      return fresh;
    },
    async saveDmeDraft({ commit, state }) {
      if (!state.appointmentId) return null;
      const appointmentId = state.appointmentId;
      const api = Vue.prototype.$api.prescriptionDme;

      const dmeBundles = serializeDmeDraftsForApi(state.draftDmeSelections)
        .filter(b => b && typeof b.dmeCode === 'string');
      await Promise.all(dmeBundles.map((bundle) => {
        if (bundle._delete) {
          return api.removeDmeSelection(appointmentId, bundle.dmeCode);
        }
        const { dmeCode, _delete: _del, ...selectionPayload } = bundle;
        return api.upsertDmeSelection(appointmentId, dmeCode, selectionPayload);
      }));

      commit('CLEAR_DRAFT_DME_SELECTIONS');

      const refetched = await api.getCase(appointmentId);
      const fresh = refetched.data?.payload ?? refetched.data;
      commit('SET_CASE', fresh);
      return fresh;
    },
    async previewPdf({ state }) {
      if (!state.appointmentId) return null;
      const response = await Vue.prototype.$api.prescriptionDme.previewPdf(state.appointmentId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    },
    async signAndSubmit({ commit, state }, { signature }) {
      if (!state.appointmentId) return null;
      await Vue.prototype.$api.prescriptionDme.signPacket(state.appointmentId, { signature });
      const refetched = await Vue.prototype.$api.prescriptionDme.getCase(state.appointmentId);
      const fresh = refetched.data?.payload ?? refetched.data;
      commit('SET_CASE', fresh);
      return fresh;
    },
    async saveComplianceReview({ commit, state }, payload) {
      if (!state.appointmentId) return null;
      const body = { ...(payload || {}) };
      const draft = state.draftCase || {};
      const hasDraft = draft && Object.keys(draft).length > 0;
      if (hasDraft) body.caseUpdates = draft;

      const medBundles = serializeMedicationDraftsForApi(state.draftMedicationSelections);
      if (medBundles.length) body.medicationSelections = medBundles;

      const dmeBundles = serializeDmeDraftsForApi(state.draftDmeSelections);
      if (dmeBundles.length) body.dmeSelections = dmeBundles;

      await Vue.prototype.$api.prescriptionDme.saveComplianceReview(state.appointmentId, body);

      commit('CLEAR_DRAFT_CASE');
      commit('CLEAR_DRAFT_MED_SELECTIONS');
      commit('CLEAR_DRAFT_DME_SELECTIONS');

      const refetched = await Vue.prototype.$api.prescriptionDme.getCase(state.appointmentId);
      const fresh = refetched.data?.payload ?? refetched.data;
      commit('SET_CASE', fresh);
      return fresh;
    },
    setCurrentScreen({ commit }, screen) {
      commit('SET_CURRENT_SCREEN', screen);
    },
    reset({ commit }) {
      commit('RESET');
    },
  },
  getters: {
    isBlocked: (state) => state.case && state.case.status === 'blocked',
    effectiveClinicalSnapshot: (state) => {
      const base = (state.case && state.case.clinicalSnapshot) || {};
      const draft = (state.draftCase && state.draftCase.clinicalSnapshot) || {};
      return { ...base, ...draft };
    },
    effectiveReviews: (state) => {
      const base = state.case || {};
      const draft = state.draftCase || {};
      return {
        reviewDemographics: draft.reviewDemographics ?? !!base.reviewDemographics,
        reviewCaseInfo: draft.reviewCaseInfo ?? !!base.reviewCaseInfo,
        reviewAllergies: draft.reviewAllergies ?? !!base.reviewAllergies,
        reviewDiagnosis: draft.reviewDiagnosis ?? !!base.reviewDiagnosis,
        reviewNotFederalPayor: draft.reviewNotFederalPayor ?? !!base.reviewNotFederalPayor,
      };
    },
    effectiveFederalPayorFlag: (state) => {
      const draft = state.draftCase || {};
      if (typeof draft.federalPayorFlag === 'string') return draft.federalPayorFlag;
      return (state.case && state.case.federalPayorFlag) || 'unknown';
    },
    icd10FromCase: (state, getters) => {
      const fromClinical = getters.effectiveClinicalSnapshot.icd10Codes;
      if (Array.isArray(fromClinical) && fromClinical.length) return fromClinical;
      const fromSoap = state.case && state.case.soapNote && Array.isArray(state.case.soapNote.icd10Codes)
        ? state.case.soapNote.icd10Codes : [];
      return fromSoap;
    },
    persistedMedicationSelectionByCode: (state) => (classCode) => {
      const list = (state.case && state.case.medicationSelections) || [];
      return list.find(s => s.classCode === classCode) || null;
    },
    persistedDmeSelectionByCode: (state) => (dmeCode) => {
      const list = (state.case && state.case.dmeSelections) || [];
      return list.find(s => s.dmeCode === dmeCode) || null;
    },
    effectiveMedicationSelections: (state) => {
      const base = (state.case && state.case.medicationSelections) || [];
      const drafts = state.draftMedicationSelections || {};
      const draftCodes = new Set(Object.keys(drafts));
      const result = [];
      base.forEach((sel) => {
        if (!draftCodes.has(sel.classCode)) result.push(sel);
      });
      Object.entries(drafts).forEach(([classCode, value]) => {
        if (value === null || value === undefined) return;
        result.push({ ...value, classCode });
      });
      return result;
    },
    effectiveDmeSelections: (state) => {
      const base = (state.case && state.case.dmeSelections) || [];
      const drafts = state.draftDmeSelections || {};
      const draftCodes = new Set(Object.keys(drafts));
      const result = [];
      base.forEach((sel) => {
        if (!draftCodes.has(sel.dmeCode)) result.push(sel);
      });
      Object.entries(drafts).forEach(([dmeCode, value]) => {
        if (value === null || value === undefined) return;
        result.push({ ...value, dmeCode });
      });
      return result;
    },
    findMedicationSelection: (state, getters) => (classId) => (
      getters.effectiveMedicationSelections.find(s => s.classId === classId) || null
    ),
    findDmeSelection: (state, getters) => (dmeId) => (
      getters.effectiveDmeSelections.find(s => s.dmeId === dmeId) || null
    ),
  },
};
